/**
 * Shared helper functions for rendering questionnaire answers.
 *
 * These utilities are used by both CompanyAnswers and IndividualAnswers
 * to avoid duplicating common answer-formatting and validation logic.
 */
import { AnswerDto } from "../../../../types/CompanyAnswersTypes";

/**
 * Translates a boolean answer value into Icelandic text.
 *   true  → "Já"
 *   false → "Nei"
 *   null  → "" (unanswered)
 */
export const translateBooleanAnswer = (value: boolean | null): string => {
    if (value === true) return 'Já';
    if (value === false) return 'Nei';
    return '';
};

/**
 * Builds combined answer text from a boolean value and optional free-text.
 * The boolean part is translated to Icelandic (Já/Nei), then the text
 * is appended with a comma separator when both parts are present.
 *
 * @param answerBoolean - true = "Já", false = "Nei", null = no boolean part
 * @param answerText    - Additional text to append after the boolean
 * @returns Formatted answer string
 */
export const buildAnswerText = (answerBoolean: boolean | null, answerText: string): string => {
    let text = translateBooleanAnswer(answerBoolean);

    if (answerText?.length > 0) {
        text = text === '' ? answerText : `${text}, ${answerText}`;
    }
    return text;
};

/**
 * Checks whether any answer in a question block (main question or its
 * subquestions) is flagged as wrong. When true, every answer cell in the
 * block receives extra left-padding so that the warning icon can be
 * placed consistently without shifting the text.
 *
 * @param question - The parent AnswerDto whose block should be inspected
 * @returns true if any answer in the block is marked as wrong
 */
export const hasAnyWrongAnswer = (question: AnswerDto): boolean => {
    if (question.isWrong) return true;

    if (question.subQuestions && question.subQuestions.length > 0) {
        return question.subQuestions.some(subQ => hasAnswer(subQ) && subQ.isWrong);
    }

    return false;
};

/**
 * Determines whether a question has been answered.
 *
 * A question is considered "answered" if any of its answer fields
 * contain a value. When a parent question's answer causes its
 * subquestions to be skipped (e.g. parent answered "Nei"), the backend
 * still includes the subquestions in the DTO but with all fields in
 * their default/empty state. This helper detects those unanswered
 * subquestions so they can be filtered out of the answer sheet.
 */
export const hasAnswer = (question: AnswerDto): boolean => {
    if (question.answerBoolean !== null) return true;
    if (question.answertext?.length > 0) return true;
    if (question.optionTextAnswer?.length > 0) return true;
    return false;
};

/**
 * Resolves the display text when a question has an optionTextAnswer.
 *
 * Some questions offer predefined answer options (radio/select). When the
 * user picks one, its label is stored in `optionTextAnswer`. The special
 * values "Other" / "Annað" mean the user chose the free-text option, so
 * we fall back to the regular `answertext` field instead.
 *
 * Returns `null` when no optionTextAnswer is set, signalling the caller
 * to use their own default rendering path.
 *
 * @param question - The AnswerDto to inspect
 * @returns The resolved option text, or null if no option was selected
 */
export const getOptionTextAnswer = (question: AnswerDto): string | null => {
    if (!question.optionTextAnswer) return null;

    // "Other" / "Annað" means the user typed a free-text answer
    if (question.optionTextAnswer === "Other" || question.optionTextAnswer === "Annað") {
        return buildAnswerText(question.answerBoolean, question.answertext);
    }

    return question.optionTextAnswer;
};
